import { useEffect, useMemo } from "react"

import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import { serializeData } from "../../../../utils/utils"
import { selectActiveBranchId } from "../../../Branches/redux/branches.slice.ts"
import { fetchRecentProjects } from "../../redux/dashboard.thunks.ts"
import type { DashboardProjectFilters } from "../../redux/dashboard.types.ts"
import { fetchPortfolio, setTeamScope } from "../../redux/portfolio.slice.ts"
import { resolveWidget } from "../../redux/widgets.slice.ts"
import type { WidgetInstanceV4 } from "../../redux/widgets.types.ts"
import { instanceValueKey, slugFor } from "../Portfolio/widgetsResponseAdapter.ts"
import type { ActivityItem } from "./_helpers.ts"

const instanceFor = (
  type: string,
  dataSource: string,
  metric: string
): WidgetInstanceV4 => {
  const slug = slugFor(dataSource, metric, type)
  return { id: slug, slug, type, dataSource, config: { metric } }
}

const OVERDUE_TASKS = instanceFor("kpi", "tasks", "overdue_tasks")
const UPCOMING_MILESTONES = instanceFor("table", "projects", "upcoming_milestones")
const ACTIVITY_FEED = instanceFor("feed", "activity_feed", "activity_feed")

const SECTIONS = [OVERDUE_TASKS, UPCOMING_MILESTONES, ACTIVITY_FEED]

export const useMobileHomeData = () => {
  const dispatch = useAppDispatch()

  const portfolioWindow = useAppSelector((state) => state.portfolio.window)
  const bundle = useAppSelector((state) => state.portfolio.bundle)
  const portfolioStatus = useAppSelector((state) => state.portfolio.status)
  const resolvedByKey = useAppSelector((state) => state.widgets.resolvedByKey)
  const projects = useAppSelector((state) => state.dashboard.projects?.results)
  const projectFilters = useAppSelector((state) => state.dashboard.projectFilters)

  const isUnavailable = portfolioStatus === "unavailable"
  const activeBranchId = useAppSelector(selectActiveBranchId)

  // Must run, and commit to the store, before `fetchPortfolio`/`resolveWidget`
  // read `state.portfolio.teamScope` — otherwise mobile Home falls back to
  // the server's no-filter default (every branch the caller can see) and
  // shows the same company-wide numbers whichever branch is selected.
  useEffect(() => {
    dispatch(setTeamScope(activeBranchId ? [activeBranchId] : []))
  }, [dispatch, activeBranchId])

  useEffect(() => {
    void dispatch(fetchPortfolio(portfolioWindow))
  }, [dispatch, portfolioWindow, activeBranchId])

  useEffect(() => {
    if (isUnavailable) return
    for (const instance of SECTIONS) {
      void dispatch(resolveWidget({ instance, window: portfolioWindow }))
    }
  }, [dispatch, portfolioWindow, isUnavailable, activeBranchId])

  useEffect(() => {
    const filters = serializeData({
      ...projectFilters,
      ordering: "-updated"
    }) as DashboardProjectFilters
    void dispatch(fetchRecentProjects(filters))
  }, [dispatch, projectFilters])

  const tasksDue = useMemo(() => {
    const payload = resolvedByKey[instanceValueKey(OVERDUE_TASKS)]
      ?.payload as { value?: unknown; has_data?: boolean } | undefined
    if (!payload || payload.has_data === false || payload.value == null) return "0"
    return String(payload.value)
  }, [resolvedByKey])

  const milestoneRows = useMemo(() => {
    const payload = resolvedByKey[
      instanceValueKey(UPCOMING_MILESTONES)
    ]?.payload as { rows?: Array<Record<string, unknown>> } | undefined
    return payload?.rows ?? []
  }, [resolvedByKey])

  const activityItems = useMemo(() => {
    const payload = resolvedByKey[instanceValueKey(ACTIVITY_FEED)]
      ?.payload as { items?: ActivityItem[] } | undefined
    return payload?.items ?? []
  }, [resolvedByKey])

  return {
    isUnavailable,
    isLoading: portfolioStatus === "loading" && !bundle,
    allValues: bundle?.all_values,
    projects: projects ?? [],
    tasksDue,
    milestoneRows,
    activityItems
  }
}
